import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Hotel, Pencil } from "lucide-react";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import { FileUploadButton } from "@/components/FileUploadButton";
import { TravelerAssign, TravelerChips } from "@/components/TravelerAssign";
import { formatError } from "@/lib/api";
import { useCollection } from "./useCollection";

const empty = { name: "", address: "", check_in: "", check_out: "", confirmation: "", room_type: "", reservation_file_id: null, traveler_ids: [], notes: "" };

export default function HotelsSection({ tripId, onChange }) {
  const { items, loading, create, update, remove } = useCollection(tripId, "hotels", onChange);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  function openNew() { setEditingId(null); setForm(empty); setOpen(true); }
  function openEdit(item) {
    setEditingId(item.id);
    setForm({ ...empty, ...Object.fromEntries(Object.entries(item).filter(([k]) => k in empty)) });
    setOpen(true);
  }
  function handleOpenChange(v) { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }

  async function save() {
    if (!form.name) { toast.error("Hotel name required"); return; }
    setSaving(true);
    try {
      if (editingId) { await update(editingId, form); toast.success("Hotel updated"); }
      else { await create(form); toast.success("Hotel added"); }
      handleOpenChange(false);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div><h3 className="font-display text-xl">Hotels</h3><p className="text-sm text-stone-500">Bookings and stays.</p></div>
        <Button className="rounded-full bg-primary text-primary-foreground" onClick={openNew} data-testid="add-hotel-btn"><Plus className="w-4 h-4 mr-2" />Add hotel</Button>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent className="rounded-2xl max-w-lg">
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit hotel" : "New hotel"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Hotel name</Label><Input className="rounded-xl" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hotel-name-input" /></div>
              <div className="col-span-2"><Label>Address</Label><Input className="rounded-xl" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>Check-in</Label><Input type="text" inputMode="numeric" placeholder="YYYY-MM-DD" title="Format: YYYY-MM-DD" className="rounded-xl" value={form.check_in || ""} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="hotel-checkin-input" /></div>
              <div><Label>Check-out</Label><Input type="text" inputMode="numeric" placeholder="YYYY-MM-DD" title="Format: YYYY-MM-DD" className="rounded-xl" value={form.check_out || ""} onChange={(e) => setForm({ ...form, check_out: e.target.value })} data-testid="hotel-checkout-input" /></div>
              <div><Label>Confirmation</Label><Input className="rounded-xl" value={form.confirmation} onChange={(e) => setForm({ ...form, confirmation: e.target.value })} /></div>
              <div><Label>Room type</Label><Input className="rounded-xl" value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} /></div>
              <div className="col-span-2 flex items-center gap-3"><FileUploadButton label={form.reservation_file_id ? "Change photo" : "Attach reservation"} testId="hotel-file-upload" onUploaded={(id) => setForm((f) => ({ ...f, reservation_file_id: id }))} />{form.reservation_file_id && <span className="text-xs text-stone-500">Attached</span>}</div>
              <div className="col-span-2 pt-1"><TravelerAssign tripId={tripId} value={form.traveler_ids} onChange={(ids) => setForm((f) => ({ ...f, traveler_ids: ids }))} testId="hotel-travelers" /></div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => handleOpenChange(false)} className="rounded-full">Cancel</Button><Button onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-hotel-btn">{saving ? "Saving..." : editingId ? "Save changes" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="h-32 rounded-2xl bg-muted animate-pulse" /> :
       items.length === 0 ? (
        <Card className="rounded-2xl p-10 border-dashed border-border text-center" data-testid="empty-hotels"><Hotel className="w-8 h-8 mx-auto text-primary" /><p className="mt-2 text-stone-500">No hotels yet.</p></Card>
       ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((h) => (
            <Card key={h.id} className="p-5 rounded-2xl border-border" data-testid={`hotel-${h.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-primary"><Hotel className="w-4 h-4" /><span className="font-display text-lg">{h.name}</span></div>
                <div className="flex items-center gap-0.5">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(h)} className="rounded-full text-stone-400 hover:text-primary" data-testid={`edit-hotel-${h.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(h.id)} className="rounded-full text-stone-400 hover:text-destructive" data-testid={`delete-hotel-${h.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              {h.address && <div className="text-sm text-stone-500 mt-1">{h.address}</div>}
              <div className="mt-2 text-xs text-stone-500 grid grid-cols-2 gap-1">
                {h.check_in && <div>In: {h.check_in}</div>}
                {h.check_out && <div>Out: {h.check_out}</div>}
                {h.confirmation && <div>Conf: {h.confirmation}</div>}
                {h.room_type && <div>Room: {h.room_type}</div>}
              </div>
              {h.reservation_file_id && <AuthImage fileId={h.reservation_file_id} className="mt-3 w-full h-32 object-cover rounded-xl" />}
              <TravelerChips tripId={tripId} travelerIds={h.traveler_ids} />
            </Card>
          ))}
        </div>
       )}
    </div>
  );
}
